"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Eye, EyeOff } from "react-feather";

export default function LoginForm({ redirectTo }: { redirectTo?: string | null }) {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const router = useRouter();

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        const result = await signIn("credentials", {
          email,
          password,
          redirect: false,
        });
        if (result?.error) {
          setError("Invalid email or password.");
          setLoading(false);
          return;
        }
        router.push(redirectTo || "/app/history");
      }}
      className="stack gap-5"
    >
      <div className="stack gap-1.5">
        <label
          htmlFor="email"
          className="text-[1rem] uppercase tracking-[0.2em] text-gray-500 font-semibold"
        >
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          placeholder="you@thinknado.com"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          className="w-full border border-black/30 bg-white px-4 h-12 text-sm placeholder-gray-400 focus:border-black focus:outline-none focus:ring-0 disabled:opacity-50 transition-colors"
        />
      </div>

      <div className="stack gap-1.5">
        <div className="flex items-center justify-between">
          <label
            htmlFor="password"
            className="text-[1rem] uppercase tracking-[0.2em] text-gray-500 font-semibold"
          >
            Password
          </label>
          <Link
            href={`/auth/forgot-password?email=${encodeURIComponent(email)}`}
            className="text-[1rem] uppercase tracking-[0.15em] text-gray-500 hover:text-black transition-colors"
          >
            Forgot password?
          </Link>
        </div>
        <div className="relative">
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            className="w-full border border-black/30 bg-white px-4 pr-12 h-12 text-sm placeholder-gray-400 focus:border-black focus:outline-none focus:ring-0 disabled:opacity-50 transition-colors"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-400 hover:text-black transition-colors"
            tabIndex={-1}
          >
            <span className="sr-only">{showPassword ? "Hide" : "Show"} password</span>
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {error && (
        <p className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading || !email || !password}
        className="w-full border border-black bg-black text-white px-6 py-3 text-sm font-black uppercase tracking-[0.14em] hover:bg-white hover:text-black transition disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? "Signing In..." : "Sign In"}
      </button>

      <p className="text-center text-sm text-gray-600">
        Don&apos;t have an account?{" "}
        <Link
          href={
            redirectTo
              ? `/auth/sign-up?redirect=${encodeURIComponent(redirectTo)}`
              : "/auth/sign-up"
          }
          className="font-semibold text-black underline underline-offset-2 hover:text-purple transition-colors"
        >
          Create one now
        </Link>
      </p>
    </form>
  );
}
